import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styled from 'styled-components';
import DidYouKnow from '@components/shared/design/DidYouKnow';
import { PageSlice, SliceTextImageCarouselAnimated, SliceTextImageCarouselAnimatedItem } from '@contracts';

// Register ScrollTrigger with GSAP
gsap.registerPlugin(ScrollTrigger);

// Set default easing and duration for all animations here
gsap.defaults({ ease: 'power2.Out', duration: 3 });

interface Props {
  slice: PageSlice;
}

const TextImageCarouselAnimated: React.FC<Props> = ({ slice }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLDivElement | null>(null);
  const bgRef = useRef<HTMLDivElement | null>(null);
  const slideNavRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [progressAtLabels, setProgressAtLabels] = useState<number[]>([]);
  const { t } = useTranslation();
  const sliceDetails = slice.primary as SliceTextImageCarouselAnimated;
  const sliceItems = slice.items as SliceTextImageCarouselAnimatedItem[];

  function debounce(fn: (...args: any[]) => void, t: number): (...args: any[]) => void {
    let debounceTimer: NodeJS.Timeout;
    return function (...args: any[]): void {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fn(...args), t);
    };
  }

  useEffect(() => {
    // Create the timeline and ScrollTrigger instance
    const tl2 = gsap.timeline();
    let st2: ScrollTrigger;
    const activeSlideIndex = 0;

    // Function that handles responsive resizing
    // This function is called on resize(debounced) and on initial load
    const updateAnimation2 = debounce(() => {
      // Clear all inline styles to reset the animation
      gsap.set(textRef.current, { clearProps: 'all' });
      gsap.set(imageRef.current, { clearProps: 'all' });
      gsap.set(bgRef.current, { clearProps: 'all' });
      gsap.set(slideNavRef.current, { clearProps: 'all' });
      const slides = slideRefs.current;
      slides.forEach((slide: HTMLDivElement | null) => {
        if (!slide) return;
        gsap.set(slide, { clearProps: 'all' });
        gsap.set(slide.children, { clearProps: 'all' });
      });

      const windowWidth = window.innerWidth;

      // Save the current progress of the timeline
      const progress = tl2.progress();

      /*
      TODO: Explore GSAP's media queries as solution here
      Manually handling different animations for big and small screen sizes,
      this could be possible with ScrollTrigger's media queries but haven't found a way to do it yet

      Also: not using the useDesktop hook for now because it's causing issues.
      Probably because it has it's own resize event handling and it's not working well with the one I'm using here. 
      */
      if (windowWidth > 800) {
        // Window Size > 800px (Animation for big screens)

        // Clear and recreate the timeline
        tl2
          .invalidate()
          .clear()
          .addLabel('Start')
          .to({}, { duration: 1 }) // wait at start, feels better when scrolling
          .addLabel('IntroSlideEnd')
          .to(textRef.current, { opacity: 0 })
          .to(textRef.current, { width: 0 }, '>')
          .fromTo(bgRef.current, { width: '36%' }, { width: '100%' }, '<')
          .fromTo(imageRef.current, { x: 80 }, { x: 0 }, '<')
          .to(imageRef.current, { position: 'absolute' }, '>')
          .to(imageRef.current, { opacity: 0 }, '>+1')
          // .to(slideNavRef.current, { display: 'inline-flex' }, '<+0.5')
          // TODO: Hidden until we make it nice and smooth
          .to(slideNavRef.current, { opacity: 1 }, '<');

        slides.forEach((slide: HTMLDivElement | null, index: number) => {
          if (!slide) return;
          tl2
            .addLabel(`slide${index}Start`) // label used for navigation with dots
            .to(slide, { opacity: 1 }, '<+0.5')
            .fromTo(slide.children[1], { y: 20 }, { y: -10 }, '<+1');
          // Last slide shouldn't fade out
          if (index !== slides.length - 1) {
            tl2.to(slide, { opacity: 0 }, '>-0.5');
          }
        });
      } else {
        // Window Size <= 800px (Animation for small screens)

        gsap.set(textRef.current, { top: 0, height: '55vh' }); // TODO: need to find a better way to handle this

        // Clear and recreate the timeline
        tl2
          .invalidate()
          .clear()
          .addLabel('Start')
          .to({}, { duration: 1 }) // wait at start, feels better when scrolling
          .addLabel('IntroSlideEnd')
          .to(textRef.current, { opacity: 0, height: '40vh' })
          .to(imageRef.current, { opacity: 0 }, '>+1')
          // .to(slideNavRef.current, { display: 'flex' }, '<+0.5')
          // TODO: Hidden until we make it nice and smooth
          .fromTo(slideNavRef.current, { opacity: 0 }, { opacity: 1 }, '<')
          .to({}, { duration: 3 });

        slides.forEach((slide: HTMLDivElement | null, index: number) => {
          if (!slide) return;

          tl2
            .set(slide, { css: { zIndex: 1 } }, '<-3')
            .set(slide, { opacity: 1 }, '<')
            .fromTo(slide.childNodes[0], { opacity: 0 }, { opacity: 1 }, '<')
            .fromTo(slide.childNodes[1], { opacity: 0 }, { opacity: 1 }, '<+2')

            .to({}, { duration: 1.5 }) // pause at slide
            .addLabel(`slide${index}Start`, '>') // label used for navigation with dots
            .to({}, { duration: 1.5 }); // pause at slide

          if (index !== slides.length - 1) {
            tl2
              .set(slide, { css: { zIndex: 0 } })
              .to(slide.childNodes[1], { opacity: 0 }, '>+0.5')
              .to(slide.childNodes[0], { opacity: 0 }, '>');
          }
        });
      }

      // Set the progress of the timeline back to what it was before
      tl2.progress(progress);

      // Refresh the ScrollTrigger instance
      if (st2) {
        st2.refresh();
      }

      // Get the progress at each label for the navigation with Circle components
      const labelKeys: string[] = Object.keys(tl2.labels);
      const progressAtLabels: number[] = labelKeys.map((key: string) => {
        const labelTime: number = tl2.labels[key];
        const totalDuration: number = tl2.duration();
        const progressAtLabel: number = labelTime / totalDuration;

        return progressAtLabel;
      });

      setProgressAtLabels(progressAtLabels);
    }, 200); // Set the debounce time to 200ms

    window.addEventListener('resize', updateAnimation2);

    // Call once to set the initial properties
    updateAnimation2();

    // Create ScrollTrigger instance - should be after tl is created
    ScrollTrigger.config({
      ignoreMobileResize: true
    });
    st2 = ScrollTrigger.create({
      id: 'st01',
      trigger: containerRef.current,
      start: window.innerWidth > 800 ? 'center center' : 'top top',
      end: '+=5000',
      pin: true,
      animation: tl2,
      scrub: 1,
      snap: {
        snapTo: 'labelsDirectional',
        duration: { min: 0.1, max: 0.3 },
        delay: 0,
        ease: 'power1.Out'
      },
      markers: false,
      invalidateOnRefresh: true
      // onUpdate: (self: gsap.plugins.ScrollTriggerInstance) => {
      //   // Calculate and set the active slide index dynamically
      //   let labelKeys: string[] = Object.keys(tl2.labels);
      //   let progress = self.progress + 0.05; // offset by 0.05 to better match the transition between slides
      //   for (let key of labelKeys) {
      //     let progressAtLabel = tl2.labels[key] / tl2.duration();
      //     if (progress >= progressAtLabel) {
      //       activeSlideIndex = labelKeys.indexOf(key) + 1;
      //       setActiveSlide(activeSlideIndex);
      //     } else {
      //       break;
      //     }
      //   }
      // }
    });

    return () => window.removeEventListener('resize', updateAnimation2);
  }, [sliceItems.length]); // add to dependency array to recalculate when the number of slides changes

  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 800 : false; // window. doesn't exist during SSR, check for that
  return (
    <TextImageCarouselContainer ref={containerRef}>
      <Wrapper>
        <TextImageCarouselText ref={textRef}>
          <Overline>{sliceDetails.overline}</Overline>
          <h3>{sliceDetails.heading.text}</h3>
          <div
            dangerouslySetInnerHTML={{
              __html: sliceDetails.intro_description.html
            }}
          />
          <DidYouKnowWrap>
            <DidYouKnow text={sliceDetails.did_you_know_text} headingText={t('components.did-you-know')} icon={'did-you-know'} centered={isMobile} />
          </DidYouKnowWrap>
        </TextImageCarouselText>
        <ImageBackground ref={bgRef} />
        <TextImageCarouselImage ref={imageRef}>
          <img src={sliceDetails.intro_image.url} alt={sliceDetails.intro_image.alt} />
        </TextImageCarouselImage>
        {sliceItems.map((item: any, index: number) => (
          <CarouselSlide data-testid="slide" key={index} ref={(el) => (slideRefs.current[index] = el)}>
            <TextImageCarouselImage>
              <img src={item.image.url} alt={item.image.alt} />
            </TextImageCarouselImage>

            <TextImageCarouselText>
              <Overline>{item.overline}</Overline>
              <h3>{item.heading.text}</h3>
              <div dangerouslySetInnerHTML={{ __html: item.slide_description.html }} />
            </TextImageCarouselText>
          </CarouselSlide>
        ))}
        <CarouselSlideNav ref={slideNavRef}>
          {sliceItems.map((_: any, index: number) => (
            <Circle
              key={index}
              onClick={() => {
                const st2 = ScrollTrigger.getById('st01');

                if (!st2) return;
                const progressAtLabel = progressAtLabels[index];

                // Calculate here, ScrollTrigger instance should be initialised
                const scrollPos = progressAtLabel * (st2.end - st2.start) + st2.start;

                window.scrollTo(0, scrollPos);
              }}
              $active={activeSlide === index + 1}
            />
          ))}
        </CarouselSlideNav>
      </Wrapper>
    </TextImageCarouselContainer>
  );
};

export default TextImageCarouselAnimated;

const TextImageCarouselContainer = styled.section`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  overflow: hidden;
`;

const Wrapper = styled.div`
  width: 90vw;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  padding: 10rem 0rem;
  max-height: 100vh;

  @media (max-width: 800px) {
    flex-direction: column;
    height: 100%;
    padding: 6rem 1rem 0;
    text-align: center;
    justify-content: flex-start;
  }
`;

const TextImageCarouselText = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
  width: 70%;
  max-width: 50%;

  & p {
    font-size: 1rem;

    @media (max-width: 450px) {
      font-size: 0.85rem;
    }

    @media (max-width: 370px) {
      font-size: 0.75rem;
    }
  }

  & h3 {
    font-family: 'Domaine';
    font-size: 2rem;
    font-style: normal;
    font-weight: 600;
    line-height: 130%;
    letter-spacing: -1.28px;
    padding: 0;
    margin: 0;

    @media (max-width: 450px) {
      font-size: 1.5rem;
    }
  }

  @media (max-width: 800px) {
    max-width: 100%;
    margin-bottom: 2rem;
    margin-top: 0;
    width: 100%;
    max-height: 100%;
    height: 40vh;
    justify-content: center;
    row-gap: 0.3rem;
  }

  @media (max-width: 370px) {
    height: 55vh;
  }
`;

const Overline = styled.div`
  font-family: 'BrownStd';
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
`;

const DidYouKnowWrap = styled.div`
  margin-top: 16px;
`;

const TextImageCarouselImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 300px;
  z-index: 3;
  max-height: 70vh;

  & img {
    height: 100%;
    max-height: 70vh;

    @media (max-width: 800px) {
      height: auto;
      width: 250px;
    }
  }

  @media (max-width: 800px) {
    justify-content: center;
    align-items: flex-start;
    max-height: 55vh;
  }
`;

const ImageBackground = styled.div`
  position: absolute;
  width: 36%;
  background: #e5f2e3;
  height: 60%;
  top: 20%;
  right: 0;
  z-index: -1;

  @media (max-width: 800px) {
    top: auto;
    bottom: 0;
    width: 100%;
    height: 300px;
  }

  @media (max-width: 370px) {
    height: 200px;
  }
`;

const CarouselSlide = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: absolute;
  opacity: 0;
  max-width: 1200px;
  column-gap: 3rem;
  width: 100%;

  @media (max-width: 800px) {
    flex-direction: column-reverse;
    padding: 0 2rem;
    max-height: 100vh;
  }
`;

const CarouselSlideNav = styled.div`
  display: none; /* TODO: Hidden until we make it nice and smooth */
  flex-direction: row;
  align-items: center;
  margin-bottom: -19rem;
  position: relative;
  z-index: 5;
  height: 100%;
  width: 100%;
  min-width: 100%;
  padding-left: calc(300px + 2.5rem);

  @media (max-width: 800px) {
    position: absolute;
    padding: 0;
    height: auto;
    top: 435px;
    justify-content: center;
  }

  @media (max-width: 370px) {
    top: 395px;
  }
`;

const Circle = styled.div<{ $active: boolean }>`
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: ${(props) => (props.$active ? '#EBA920' : '#D3EAD1')};
  margin: 0 0.5rem;
  cursor: pointer;
`;
